import { reactMount } from '@/lib/reactMount'
import { useRef } from 'react'
import { Button } from '@/components/Button'
import { ArrowRight } from '@/components/Icons'
import { getHeadingClasses } from '@/lib/headings'

const disclaimerStyles = `
    ._rte-disclaimer ul,
    ._rte-disclaimer ol {
        margin-top: 0.5em;
        margin-bottom: 0.5em;
        padding-left: 1.25em;
    }
    ._rte-disclaimer ul { list-style-type: disc; }
    ._rte-disclaimer ol { list-style-type: decimal; }
    ._rte-disclaimer li { display: list-item; margin-bottom: 0.25em; }
    ._rte-disclaimer a { text-decoration: underline; text-underline-offset: 2px; transition: color 0.15s ease, opacity 0.15s ease; }
    ._rte-disclaimer a:hover { opacity: 0.75; }
`

export const contentHeroBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'light'
  const backgroundColor = umbraco.BlockBackground ? `#${umbraco.BlockBackground}` : '#ffffff'

  // Button logic matching standaloneCtaBlock pattern
  const primary = umbraco.Button
  const secondary = umbraco.SecondaryButton
  const tertiary = umbraco.TertiaryButton
  const hyperlink = umbraco.ButtonText

  const buttons = []

  // Primary
  if (primary) {
    buttons.push({
      ...primary,
      variant: 'primary',
      size: 'lg'
    })
  }

  // Secondary preferred over tertiary
  if (secondary) {
    buttons.push({
      ...secondary,
      variant: 'secondary',
      size: 'lg'
    })
  } else if (tertiary) {
    buttons.push({
      ...tertiary,
      variant: 'primary-alt',
      size: 'lg'
    })
  }
  // Hyperlink only if nothing else exists
  else if (hyperlink) {
    buttons.push({
      ...hyperlink,
      variant: 'link',
      size: 'lg'
    })
  }

  // Default to <h2>; promote to <h1> only when the server marks this block as first on the page.
  const isFirstBlockRaw = dataset?.isFirstBlock
  const Heading = isFirstBlockRaw === undefined
    ? 'h2'
    : (isFirstBlockRaw === 'true' ? 'h1' : 'h2')

  const headingClasses = getHeadingClasses(umbraco.TitleHeadingSize, { fallback: 'small' })

  return (
    <>
      <style>{disclaimerStyles}</style>
      {
        (umbraco.Media && umbraco.Media.Image)
          ? (
            <div
              data-theme={theme}
              style={{ backgroundColor }}
              className='_contentHeroBlock-section relative isolate overflow-hidden lg:px-10 py-12 md:py-0'
              id={umbraco.AnchorTarget || undefined}
            >
              <div className='mx-auto max-w-7xl md:flex items-center'>

                <div className={`px-4 py-6 sm:px-6 md:w-[45%] xl:w-[35%] ${umbraco.MediaLeft ? 'md:order-2' : ''}`}>
                  <div className='mx-auto md:max-w-lg lg:mx-0'>
                    {
                      umbraco.Label &&
                        <span className='pb-6 uppercase block text-xs font-extrabold text-[--theme-highlight] tracking-[2.025px]'>{umbraco.Label}</span>
                    }
                    {
                      umbraco.Title &&
                        <Heading className={`${headingClasses} font-extrabold tracking-tight text-balance`}>
                          {umbraco.Title}
                        </Heading>
                    }
                    {
                      umbraco.Summary &&
                        <div
                          className='mt-5 text-balance text-base font-extrabold text-[--base-text]'
                          dangerouslySetInnerHTML={{ __html: umbraco.Summary }}
                        />
                    }
                    {umbraco.TitleSummaryList && umbraco.TitleSummaryList.length > 0 &&
                      <ul className='flex flex-col gap-7 mt-9 first:mt-0'>
                        {/* Loop items here */}
                        {umbraco.TitleSummaryList.map((item, index) => (
                          <li className='flex flex-col gap-3' key={index}>
                            {item.Title && <h3 className='text-2xl font-extrabold'>{item.Title}</h3>}
                            {item.Summary && <p className='text-base text-default-800'>{item.Summary}</p>}
                          </li>
                        ))}
                      </ul>}
                    {(umbraco.ListTitle || umbraco.List.length > 0) &&
                      <div className='flex flex-col gap-y-2 mt-9 first:mt-0'>
                        {
                          umbraco.ListTitle &&
                            <h3 className='text-balance text-base font-semibold text-[--base-text]'>{umbraco.ListTitle}</h3>
                        }
                        {umbraco.List.length > 0 && (
                          <>
                            <ul className={`${umbraco.ListTitle ? 'mt-5' : ''} text-xl font-semibold text-[--base-text] flex flex-col gap-y-[18px]`}>
                              {umbraco.List.map((item, index) => (
                                <li className='flex items-start gap-3' key={index}>
                                  <svg className='shrink-0 relative' xmlns='http://www.w3.org/2000/svg' width='25' height='25' viewBox='0 0 25 25' fill='none'>
                                    <circle cx='12.2598' cy='12.3809' r='12' fill='#36C2FA' />
                                    <path d='M10.4308 17.4924L5.85352 12.9151L6.99784 11.7708L10.4308 15.2038L17.7986 7.83594L18.9429 8.98026L10.4308 17.4924Z' fill='white' />
                                  </svg>
                                  <span className='text-base font-semibold text-[--base-text]'>{item}</span>
                                </li>
                              ))}
                            </ul>

                            {umbraco.Disclaimer && (
                              <div
                                className='mt-5 text-xs text-default-500 font-bold leading-snug _rte-disclaimer'
                                dangerouslySetInnerHTML={{ __html: umbraco.Disclaimer }}
                              />
                            )}
                          </>
                        )}
                      </div>}
                    {buttons.length > 0 &&
                      <div data-theme='blue' className='mt-10 flex flex-wrap items-center gap-y-2.5 gap-x-6'>
                        {buttons.map((btn, index) => (
                          <Button
                            key={index}
                            href={btn.Url}
                            target={btn.Target}
                            variant={btn.variant}
                            size={btn.size}
                            componentName={btn.Url?.includes('#apply-dialog') ? 'Content Hero Block' : null}
                          >
                            {btn.variant === 'primary-alt'
                              ? (
                                <span className='flex items-center'>{btn.Name} <ArrowRight className='translate-x-1 translate-y-[3px]' /></span>
                                )
                              : (
                                  btn.Name
                                )}
                          </Button>
                        ))}
                      </div>}
                  </div>
                </div>

                <div className='md:w-[55%] xl:w-[65%] px-4 pt-8 pb-10 md:py-12'>
                  <div className='relative aspect-contentHero w-[66.666666%] max-w-[478px] mx-auto'>
                    {
                      umbraco.Media && umbraco.Media.Image &&
                        <>
                          {/* Main image - use Media if available, otherwise fallback */}
                          <img
                            alt={umbraco.Media?.Image?.AltText || ''}
                            src={umbraco.Media?.Image?.Url}
                            className='absolute inset-0 w-full h-full object-cover'
                          />

                          {/* Left overlay image */}
                          {umbraco.LeftMedia && umbraco.LeftMedia.Image && (
                            <div className='absolute w-[41.25%] top-[10%] left-0 -translate-x-[60%] aspect-contentHero'>
                              <img
                                alt={umbraco.LeftMedia.Image.AltText || ''}
                                src={umbraco.LeftMedia.Image.Url}
                                className='absolute inset-0 w-full h-full object-cover'
                              />
                            </div>
                          )}

                          {/* Right overlay image */}
                          {umbraco.RightMedia && umbraco.RightMedia.Image && (
                            <div className='absolute w-[41.25%] right-0 bottom-[10%] translate-x-[60%] aspect-contentHero'>
                              <img
                                alt={umbraco.RightMedia.Image.AltText || ''}
                                src={umbraco.RightMedia.Image.Url}
                                className='absolute inset-0 w-full h-full object-cover'
                              />
                            </div>
                          )}
                        </>
                    }
                  </div>
                </div>

              </div>
            </div>
            )
          : (
            <div
              data-theme={theme}
              style={{ backgroundColor }}
              className='_block-section _contentHeroBlock-section-no-image relative isolate py-10 lg:py-16 text-[--theme-text]'
              id={umbraco.AnchorTarget || undefined}
            >
              <div className='mx-auto max-w-5xl'>
                <div className='px-4 py-6 sm:px-6 lg:px-8 text-center'>
                  {
                            umbraco.Label &&
                              <span className='pb-6 uppercase block text-xs font-extrabold text-[--theme-highlight] tracking-[2.025px]'>{umbraco.Label}</span>
                          }
                  {
                            umbraco.Title &&
                              <Heading className='text-4xl lg:text-6xl font-extrabold tracking-tight'>
                                {umbraco.Title}
                              </Heading>
                          }
                  {
                            umbraco.Summary &&
                              <div
                                className='mt-5 text-pretty text-base font-extrabold text-[--base-text]'
                                dangerouslySetInnerHTML={{ __html: umbraco.Summary }}
                              />
                          }
                  {(umbraco.ListTitle || umbraco.List) &&
                    <div className='mt-12 flex flex-col gap-y-2 pb-4 last:pb-0'>
                      {
                                umbraco.ListTitle &&
                                  <h3 className='text-balance text-base font-semibold text-[--base-text]'>{umbraco.ListTitle}</h3>
                              }
                      {
                                umbraco.List &&
                                  <ul className='mt-5 text-xl font-semibold text-[--base-text] items-center flex flex-col gap-y-[18px]'>
                                    {umbraco.List.map((item, index) => (
                                      <li className='inline-flex items-start gap-3' key={index}>
                                        <svg className='shrink-0 relative' xmlns='http://www.w3.org/2000/svg' width='25' height='25' viewBox='0 0 25 25' fill='none'>
                                          <circle cx='12.2598' cy='12.3809' r='12' fill='#36C2FA' />
                                          <path d='M10.4308 17.4924L5.85352 12.9151L6.99784 11.7708L10.4308 15.2038L17.7986 7.83594L18.9429 8.98026L10.4308 17.4924Z' fill='white' />
                                        </svg>
                                        <span className='text-base font-semibold text-[--base-text]'>{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                              }
                    </div>}

                  {buttons.length > 0 &&
                    <div data-theme='blue' className='mt-5 flex flex-col md:flex-row items-center justify-center gap-y-2.5 gap-x-6'>
                      {buttons.map((btn, index) => (
                        <Button
                          key={index}
                          href={btn.Url}
                          target={btn.Target}
                          variant={btn.variant}
                          size={btn.size}
                          componentName={btn.Url?.includes('#apply-dialog') ? 'Content Hero Block' : null}
                        >
                          {btn.variant === 'primary-alt'
                                    ? (
                                      <span className='block relative pr-7'>{btn.Name} <ArrowRight className='absolute -right-0.5 top-1/2 -mt-[11px]' /></span>
                                      )
                                    : (
                                        btn.Name
                                      )}
                        </Button>
                      ))}
                    </div>}
                </div>
              </div>
            </div>
            )
            }
    </>
  )
}

reactMount('contentHeroBlock', contentHeroBlock)
